import { useCallback, useMemo } from 'react';

import {
  GetMachinesQuery,
  GetMachinesQueryVariables,
  MachinesOrderByInput,
  SortOrder,
  useGetMachinesQuery,
} from '../../gql/generated';
import {
  MachineFilters,
  useMachineFilters,
} from '../../hooks/useMachineFilters';
import { useTableState } from '../../hooks/useTableState';
import { Machine } from '../../types/machines/machine.types';
import {
  computeEffectiveColumnVisibility,
  getDefaultColumnVisibility,
} from './machineColumnConfig';
import { mapMachineRow } from './mapMachineRow';

export function useMachinesTable() {
  const { filters, graphQLFilter } = useMachineFilters();

  const tableState = useTableState<
    Machine,
    MachineFilters,
    GetMachinesQuery,
    GetMachinesQueryVariables
  >({
    useQuery: useGetMachinesQuery,
    dataAccessor: (data) => {
      // Transform GraphQL data to match our table structure
      // All properties are flattened for easy table access
      return data?.machines.rows.map(mapMachineRow);
    },
    graphQLFilter,
    externalFilters: filters,
    buildOrderBy: (sorting): MachinesOrderByInput => {
      if (sorting.length === 0) return {};

      const firstSort = sorting[0];
      return {
        [firstSort.id]: firstSort.desc ? SortOrder.desc : SortOrder.asc,
      } as MachinesOrderByInput;
    },
    localStoragePrefix: 'machines',
    defaultPageSize: 20,
    defaultColumnVisibility: getDefaultColumnVisibility(),
  });

  // Compute effective column visibility - columns with active filters/sorting are forced visible
  const effectiveColumnVisibility = useMemo(
    () =>
      computeEffectiveColumnVisibility(
        tableState.columnVisibility,
        filters,
        tableState.sorting
      ),
    [tableState.columnVisibility, filters, tableState.sorting]
  );

  // Calculate total count and pages from GraphQL response
  const totalCount = useMemo(() => {
    return tableState.rawData?.machines?.count ?? 0;
  }, [tableState.rawData]);

  const totalPages = useMemo(() => {
    return totalCount > 0
      ? Math.ceil(totalCount / tableState.pagination.pageSize)
      : 0;
  }, [totalCount, tableState.pagination.pageSize]);

  const selectedMachineIds = useMemo(
    () =>
      Object.keys(tableState.rowSelection).filter(
        (id) => tableState.rowSelection[id]
      ),
    [tableState.rowSelection]
  );

  const setPageIndex = useCallback(
    (page: number) => {
      tableState.onPaginationChange({
        pageIndex: page,
        pageSize: tableState.pagination.pageSize,
      });
    },
    [tableState]
  );

  const setPageSize = useCallback(
    (size: number) => {
      tableState.onPaginationChange({
        pageIndex: 0,
        pageSize: size,
      });
    },
    [tableState]
  );

  const setSelectedMachineIds = useCallback(
    (ids: string[]) => {
      const newSelection = ids.reduce(
        (acc, id) => ({ ...acc, [id]: true }),
        {}
      );
      tableState.onRowSelectionChange(newSelection);
    },
    [tableState]
  );

  return {
    ...tableState,
    // Override columnVisibility with effective visibility (columns with active filters/sorting are forced visible)
    columnVisibility: effectiveColumnVisibility,
    machines: tableState.data,
    refetch: tableState.refetch,
    totalCount,
    totalPages,
    pageIndex: tableState.pagination.pageIndex,
    pageSize: tableState.pagination.pageSize,
    filters,
    setPageIndex,
    setPageSize,
    selectedMachineIds,
    setSelectedMachineIds,
    setSorting: tableState.onSortingChange,
    setColumnVisibility: tableState.onColumnVisibilityChange,
    setColumnOrder: tableState.onColumnOrderChange,
    setColumnPinning: tableState.onColumnPinningChange,
  };
}
